/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Plus, Search, Activity, Database, Radio, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import NoAccess from "../components/NoAccess";
import TextInputField from "../components/Fields/TextInputField";

import { API_BASE, apiHeaders } from "../config/api";
const adminFetch = (url, options = {}) => fetch(url, { ...options, headers: apiHeaders(options.headers) });

const numberFormat = new Intl.NumberFormat("en-US");

function fmtInt(value) {
  return numberFormat.format(Number(value) || 0);
}

function fmtDate(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function coverageTone(value) {
  const percent = Number(value) || 0;
  if (percent >= 95) return "complete";
  if (percent >= 50) return "partial";
  return "incomplete";
}

const JOB_STAGE_LABELS = {
  queued: "Queued",
  resolving_channel: "Resolving channel",
  enumerating_uploads: "Finding upload history",
  fetching_video_stats: "Fetching video metadata & statistics",
  refreshing_channel: "Refreshing channel statistics",
  scanning_uploads: "Scanning newest uploads",
  fetching_new_videos: "Fetching new video metadata & statistics",
  refreshing_channel: "Refreshing channel statistics",
  loading_videos: "Loading indexed videos",
  fetching_stats: "Fetching current video statistics",
  writing_snapshots: "Writing daily snapshots",
  writing_database: "Writing to database",
  creating_baseline: "Creating baseline snapshots",
  complete: "Complete",
  failed: "Failed",
};

function jobStageLabel(stage) {
  return JOB_STAGE_LABELS[stage] || String(stage || "Working").replace(/_/g, " ");
}

function Coverage({ channel }) {
  const percent = Math.min(100, Math.max(0, Number(channel.coverage_percent) || 0));
  const tone = coverageTone(percent);
  return (
    <div className={`yt-admin-coverage ${tone}`}>
      <div className="yt-admin-coverage-top">
        <strong>{fmtInt(channel.indexed_video_count)} / {fmtInt(channel.reported_video_count)}</strong>
        <span>{percent.toFixed(2)}%</span>
      </div>
      <div className="yt-admin-progress-track">
        <div className="yt-admin-progress-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone = "blue" }) {
  return (
    <div className={`yt-admin-summary-card ${tone}`}>
      <div className="yt-admin-summary-icon"><Icon size={16} strokeWidth={2.2} /></div>
      <div>
        <div className="yt-admin-summary-label">{label}</div>
        <div className="yt-admin-summary-value">{value}</div>
      </div>
    </div>
  );
}

function DetailPanel({ channel, onScan, scanBusy, onRefreshStats, refreshBusy }) {
  const missing = Math.max(
    (Number(channel.reported_video_count) || 0) - (Number(channel.indexed_video_count) || 0),
    0
  );

  return (
    <div className="yt-admin-detail">
      <div className="yt-admin-detail-channel-heading">
        <div>
          <span>Channel</span>
          <a href={`https://www.youtube.com/channel/${encodeURIComponent(channel.youtube_channel_id)}`} target="_blank" rel="noopener noreferrer">
            {channel.channel_name || channel.handle || "YouTube channel"}
          </a>
        </div>
        <a className="yt-admin-channel-link" href={`https://www.youtube.com/channel/${encodeURIComponent(channel.youtube_channel_id)}`} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={13} /> View YouTube Channel
        </a>
      </div>
      <div className="yt-admin-detail-grid">
        <div><span>YouTube Channel ID</span><strong>{channel.youtube_channel_id || "-"}</strong></div>
        <div><span>Internal Channel ID</span><strong>{channel.internal_channel_id}</strong></div>
        <div><span>Indexed Videos</span><strong>{fmtInt(channel.indexed_video_count)}</strong></div>
        <div><span>Reported Videos</span><strong>{fmtInt(channel.reported_video_count)}</strong></div>
        <div><span>Estimated Missing</span><strong>{fmtInt(missing)}</strong></div>
        <div><span>Coverage</span><strong className={`yt-admin-detail-coverage ${coverageTone(channel.coverage_percent)}`}>{Number(channel.coverage_percent || 0).toFixed(2)}%</strong></div>
        <div><span>Subscribers</span><strong>{fmtInt(channel.subscriber_count)}</strong></div>
        <div><span>Channel Lifetime Views</span><strong>{fmtInt(channel.channel_view_count)}</strong></div>
        <div><span>Backfill Status</span><strong>{channel.backfill_status || "-"}</strong></div>
        <div><span>Backfill Method</span><strong>{channel.backfill_method || "-"}</strong></div>
        <div><span>Active Status</span><strong>{channel.is_active ? "Active" : "Paused"}</strong></div>
        <div><span>Last Video Scan</span><strong>{fmtDate(channel.last_video_scan_at)}</strong></div>
        <div><span>Last Stats Refresh</span><strong>{fmtDate(channel.last_stats_refresh_at)}</strong></div>
        <div><span>Backfill Completion</span><strong>{fmtDate(channel.backfill_completed_at)}</strong></div>
      </div>
      <div className="yt-admin-actions">
        <div className="yt-admin-actions-label">Channel controls</div>
        <div className="yt-admin-actions-row">
          <button className={scanBusy ? "yt-admin-action-disabled" : "yt-admin-action-active"} type="button" disabled={scanBusy || !channel.internal_channel_id} onClick={() => onScan(channel)} title={scanBusy ? "Scan already running" : "Scan newest uploads"}>
            {scanBusy ? "Scan Running..." : "Scan New Videos"}
          </button>
          <button className={refreshBusy ? "yt-admin-action-disabled" : "yt-admin-action-active"} type="button" disabled={refreshBusy || !channel.internal_channel_id} onClick={() => onRefreshStats(channel)} title={refreshBusy ? "Refresh already running" : "Refresh current video statistics"}>
            {refreshBusy ? "Refresh Running..." : "Refresh Stats"}
          </button>
          {["Recover History", channel.is_active ? "Pause Tracking" : "Resume Tracking"].map((label) => (
            <button className="yt-admin-action-disabled" key={label} type="button" disabled title="Not connected yet">{label}</button>
          ))}
        </div>
        <div className="yt-admin-not-connected">Scan New Videos and Refresh Stats are active. Recover History and Pause Tracking are not connected yet.</div>
      </div>
    </div>
  );
}

export default function YouTubeAdmin() {
  const { currentUser, loadingUser } = useAuth();
  const [channels, setChannels] = useState([]);
  const [summary, setSummary] = useState({ tracked_channels: 0, active_channels: 0, total_indexed_videos: 0, total_reported_videos: 0 });
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("coverage_percent");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedId, setExpandedId] = useState(null);
  const [addValue, setAddValue] = useState("");
  const [ingestionJob, setIngestionJob] = useState(null);
  const [addError, setAddError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadChannels = async (signal) => {
    setLoading(true);
    setError("");
    try {
      const response = await adminFetch(`${API_BASE}/youtube/admin/channels`, { cache: "no-store", signal });
      if (!response.ok) throw new Error(`Channel admin request failed with HTTP ${response.status}`);
      const data = await response.json();
      setChannels(Array.isArray(data.channels) ? data.channels : []);
      setSummary({
        tracked_channels: Number(data.tracked_channels) || 0,
        active_channels: Number(data.active_channels) || 0,
        total_indexed_videos: Number(data.total_indexed_videos) || 0,
        total_reported_videos: Number(data.total_reported_videos) || 0,
      });
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(String(err.message || err));
      setChannels([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.is_admin) return undefined;
    const controller = new AbortController();
    loadChannels(controller.signal);
    return () => controller.abort();
  }, [currentUser?.is_admin]);

  const visibleChannels = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = channels.filter((channel) => {
      if (!query) return true;
      return [channel.channel_name, channel.handle, channel.youtube_channel_id, channel.backfill_status]
        .some((value) => String(value || "").toLowerCase().includes(query));
    });
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const result = typeof av === "string" ? String(av || "").localeCompare(String(bv || "")) : (Number(av) || 0) - (Number(bv) || 0);
      return sortDirection === "asc" ? result : -result;
    });
  }, [channels, search, sortKey, sortDirection]);

  const requestSort = (key) => {
    if (sortKey === key) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDirection(key === "coverage_percent" ? "asc" : "desc");
    }
  };

  const normalizedHandle = addValue.trim();
  const canAddChannel = /^@[A-Za-z0-9._-]{2,}$/.test(normalizedHandle);

  const submitChannel = async () => {
    if (!canAddChannel || submitting) return;
    setSubmitting(true);
    setAddError("");
    try {
      const response = await adminFetch(`${API_BASE}/youtube/admin/channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ handle: normalizedHandle }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `Ingestion request failed with HTTP ${response.status}`);
      setIngestionJob({ ...data, kind: "backfill" });
    } catch (err) {
      setAddError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  const scanChannel = async (channel) => {
    if (ingestionJob && ["queued", "running"].includes(ingestionJob.status)) return;
    setAddError("");
    try {
      const response = await adminFetch(`${API_BASE}/youtube/admin/channels/${channel.internal_channel_id}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `Scan request failed with HTTP ${response.status}`);
      setIngestionJob({ ...data, kind: "scan", internal_channel_id: channel.internal_channel_id, channel_name: channel.channel_name });
    } catch (err) {
      setAddError(String(err.message || err));
    }
  };

  const refreshStats = async (channel) => {
    if (ingestionJob && ["queued", "running"].includes(ingestionJob.status)) return;
    setAddError("");
    try {
      const response = await adminFetch(`${API_BASE}/youtube/admin/channels/${channel.internal_channel_id}/refresh-stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `Refresh request failed with HTTP ${response.status}`);
      setIngestionJob({ ...data, kind: "refresh", internal_channel_id: channel.internal_channel_id, channel_name: channel.channel_name });
    } catch (err) {
      setAddError(String(err.message || err));
    }
  };

  useEffect(() => {
    const jobId = ingestionJob?.job_id;
    if (!jobId || ["complete", "failed"].includes(ingestionJob.status)) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await adminFetch(`${API_BASE}/youtube/admin/jobs/${jobId}`, { cache: "no-store" });
        const data = await response.json();
        if (cancelled) return;
        setIngestionJob((previous) => ({ ...data, kind: previous?.kind, internal_channel_id: previous?.internal_channel_id }));
        if (data.status === "complete") {
          await loadChannels();
          setAddValue("");
        }
      } catch (err) {
        if (!cancelled) setIngestionJob((previous) => ({ ...previous, message: String(err.message || err) }));
      }
    };

    poll();
    const timer = window.setInterval(poll, 1200);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [ingestionJob?.job_id, ingestionJob?.status]);

  if (loadingUser) return null;
  if (!currentUser?.is_admin) return <NoAccess />;

  return (
    <div className="yt-admin-page">
      <style>{`
        .yt-admin-page { min-height: calc(var(--mg-vh) * 100); background: #000; color: #fff; padding: 24px 24px 44px; }
        .yt-admin-shell { max-width: 1660px; margin: 0 auto; }
        .yt-admin-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 18px; margin-bottom: 20px; }
        .yt-admin-kicker { color: #9aa3ad; font-size: 11px; font-weight: 900; letter-spacing: .09em; text-transform: uppercase; margin-bottom: 5px; }
        .yt-admin-title { margin: 0; font-size: 30px; line-height: 1; font-weight: 950; }
        .yt-admin-subtitle { color: #9ca3af; font-size: 12px; font-weight: 750; margin-top: 8px; }
        .yt-admin-header-tools { display: flex; align-items: center; gap: 8px; }
        .yt-admin-add { display: flex; gap: 8px; align-items: center; padding: 8px; background: #101010; border: 1px solid #292929; border-radius: 10px; }
        .yt-admin-add input { width: 190px; height: 32px; box-sizing: border-box; background: #080808; border: 1px solid #373737; border-radius: 7px; color: #fff; padding: 0 9px; outline: none; font-size: 12px; font-weight: 800; }
        .yt-admin-add button, .yt-admin-refresh { height: 32px; border: 1px solid #3a3a3a; border-radius: 7px; background: #191919; color: #777; font-size: 12px; font-weight: 900; cursor: not-allowed; display: inline-flex; align-items: center; gap: 6px; padding: 0 10px; }
        .yt-admin-add button:not(:disabled) { background: #3fa9f5; border-color: #3fa9f5; color: #000; cursor: pointer; }
        .yt-admin-add button:disabled { opacity: .62; }
        .yt-admin-ingestion { position: relative; margin-bottom: 18px; padding: 15px 16px 14px; background: linear-gradient(180deg, #151515, #0b0b0b); border: 1px solid #303030; border-radius: 10px; box-shadow: 0 14px 34px rgba(0,0,0,.3); }
        .yt-admin-ingestion.running, .yt-admin-ingestion.queued { border-color: rgba(63,169,245,.48); }
        .yt-admin-ingestion.complete { border-color: rgba(143,211,168,.5); }
        .yt-admin-ingestion.failed { border-color: rgba(228,138,138,.6); }
        .yt-admin-ingestion-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
        .yt-admin-job-badge { display: inline-flex; align-items: center; height: 24px; padding: 0 9px; border-radius: 999px; background: rgba(154,163,173,.14); border: 1px solid rgba(154,163,173,.3); color: #cfd8e3; font-size: 11px; font-weight: 950; white-space: nowrap; }
        .yt-admin-job-badge.running, .yt-admin-job-badge.queued { color: #9dd7ff; border-color: rgba(63,169,245,.45); background: rgba(63,169,245,.12); }
        .yt-admin-job-badge.complete { color: #a6e0b9; border-color: rgba(143,211,168,.45); background: rgba(143,211,168,.12); }
        .yt-admin-job-badge.failed { color: #f3aaaa; border-color: rgba(228,138,138,.5); background: rgba(228,138,138,.12); }
        .yt-admin-job-progress-row { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
        .yt-admin-job-progress-track { flex: 1; height: 8px; border-radius: 999px; background: #242424; overflow: hidden; }
        .yt-admin-job-progress-fill { height: 100%; min-width: 0; border-radius: inherit; background: #3fa9f5; transition: width 220ms ease; }
        .yt-admin-ingestion.complete .yt-admin-job-progress-fill { background: #8fd3a8; }
        .yt-admin-ingestion.failed .yt-admin-job-progress-fill { background: #e48a8a; }
        .yt-admin-job-progress-row > strong { width: 48px; color: #d5dbe3; font-size: 12px; text-align: right; }
        .yt-admin-job-message { margin-top: 8px; color: #b9c2cd; font-size: 12px; font-weight: 800; }
        .yt-admin-job-metrics { display: flex; flex-wrap: wrap; gap: 8px 18px; margin-top: 12px; color: #858e9a; font-size: 11px; font-weight: 800; }
        .yt-admin-job-metrics strong { color: #fff; font-weight: 950; margin-left: 3px; }
        .yt-admin-ingestion-success { margin-top: 10px; color: #8fd3a8; font-size: 11px; font-weight: 850; }
        .yt-admin-ingestion-failed { margin-top: 10px; color: #e48a8a; font-size: 11px; font-weight: 850; }
        .yt-admin-dismiss-job { margin-top: 12px; height: 28px; padding: 0 9px; border: 1px solid #3a3a3a; border-radius: 6px; background: #171717; color: #aab2bd; cursor: pointer; font-size: 11px; font-weight: 850; }
        .yt-admin-ingestion-error { margin-top: 9px; color: #e48a8a; font-size: 12px; font-weight: 800; }
        .yt-admin-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 18px; }
        .yt-admin-summary-card { display: flex; gap: 11px; align-items: center; padding: 15px; background: linear-gradient(180deg, #151515, #0b0b0b); border: 1px solid #242424; border-radius: 10px; box-shadow: 0 12px 30px rgba(0,0,0,.24); }
        .yt-admin-summary-icon { width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center; color: #9aa3ad; background: rgba(154,163,173,.12); }
        .yt-admin-summary-card.blue .yt-admin-summary-icon { color: #3fa9f5; background: rgba(63,169,245,.13); }
        .yt-admin-summary-label { color: #8f98a7; font-size: 11px; font-weight: 850; margin-bottom: 4px; }
        .yt-admin-summary-value { color: #fff; font-size: 25px; line-height: 1; font-weight: 950; }
        .yt-admin-status-strip { display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 420px); gap: 12px; margin-bottom: 14px; }
        .yt-admin-panel { background: linear-gradient(180deg, #121212, #090909); border: 1px solid #252525; border-radius: 10px; padding: 14px; }
        .yt-admin-panel-title { color: #fff; font-size: 13px; font-weight: 950; margin-bottom: 4px; }
        .yt-admin-panel-copy { color: #858b94; font-size: 11px; font-weight: 750; }
        .yt-admin-status-items { display: flex; gap: 18px; margin-top: 10px; }
        .yt-admin-status-item { display: flex; align-items: center; gap: 7px; color: #9ca3af; font-size: 11px; font-weight: 800; }
        .yt-admin-status-dot { width: 8px; height: 8px; border-radius: 50%; background: #9aa3ad; box-shadow: 0 0 10px rgba(154,163,173,.4); }
        .yt-admin-controls { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 10px; }
        .yt-admin-search { position: relative; width: min(420px, 100%); }
        .yt-admin-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #8b939e; pointer-events: none; }
        .yt-admin-search input { width: 100%; height: 36px; box-sizing: border-box; background: #0c0c0c; border: 1px solid #303030; border-radius: 8px; color: #fff; padding: 0 10px 0 32px; outline: none; font-size: 12px; font-weight: 800; }
        .yt-admin-count { color: #8f98a7; font-size: 12px; font-weight: 800; }
        .yt-admin-table-wrap { overflow-x: auto; border: 1px solid #252525; border-radius: 10px; background: #0b0b0b; }
        .yt-admin-table { width: 100%; border-collapse: collapse; min-width: 1120px; }
        .yt-admin-table th { text-align: left; color: #8f98a7; background: #111; border-bottom: 1px solid #292929; padding: 11px 12px; font-size: 10px; font-weight: 950; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; cursor: pointer; }
        .yt-admin-table td { padding: 12px; border-bottom: 1px solid #1c1c1c; color: #d5dbe3; font-size: 12px; font-weight: 750; vertical-align: middle; }
        .yt-admin-table tbody tr.channel-row { cursor: pointer; transition: background 140ms ease; }
        .yt-admin-table tbody tr.channel-row:hover, .yt-admin-table tbody tr.channel-row.expanded { background: #151515; }
        .yt-admin-table tbody tr:last-child td { border-bottom: 0; }
        .yt-admin-channel-name { display: flex; align-items: center; gap: 8px; color: #fff; font-weight: 950; white-space: nowrap; }
        .yt-admin-channel-chevron { color: #9aa3ad; display: inline-flex; }
        .yt-admin-handle { color: #9aa3ad; }
        .yt-admin-status { display: inline-flex; align-items: center; gap: 6px; color: #aeb7c5; white-space: nowrap; }
        .yt-admin-status::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: #9aa3ad; box-shadow: 0 0 8px rgba(154,163,173,.5); }
        .yt-admin-status.paused::before { background: #555; box-shadow: none; }
        .yt-admin-coverage { min-width: 160px; }
        .yt-admin-coverage-top { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 5px; font-size: 11px; white-space: nowrap; }
        .yt-admin-coverage-top strong { color: #d5dbe3; }
        .yt-admin-coverage-top span { font-weight: 950; }
        .yt-admin-coverage.complete .yt-admin-coverage-top span { color: #8fd3a8; }
        .yt-admin-coverage.partial .yt-admin-coverage-top span { color: #d8bd77; }
        .yt-admin-coverage.incomplete .yt-admin-coverage-top span { color: #e48a8a; }
        .yt-admin-progress-track { height: 5px; border-radius: 999px; background: #252525; overflow: hidden; }
        .yt-admin-progress-fill { height: 100%; border-radius: inherit; background: #9aa3ad; }
        .yt-admin-coverage.complete .yt-admin-progress-fill { background: #8fd3a8; }
        .yt-admin-coverage.partial .yt-admin-progress-fill { background: #d8bd77; }
        .yt-admin-coverage.incomplete .yt-admin-progress-fill { background: #e48a8a; }
        .yt-admin-detail-row td { padding: 0; background: #101010; }
        .yt-admin-detail { padding: 16px 20px 18px 48px; border-top: 1px solid #292929; box-shadow: inset 3px 0 0 #9aa3ad; }
        .yt-admin-detail-channel-heading { display: flex; justify-content: space-between; align-items: flex-end; gap: 14px; margin-bottom: 14px; }
        .yt-admin-detail-channel-heading span { display: block; color: #777f8a; font-size: 10px; font-weight: 850; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
        .yt-admin-detail-channel-heading a:not(.yt-admin-channel-link) { color: #fff; font-size: 17px; font-weight: 950; text-decoration: none; }
        .yt-admin-detail-channel-heading a:not(.yt-admin-channel-link):hover { color: #3fa9f5; }
        .yt-admin-channel-link { display: inline-flex; align-items: center; gap: 6px; color: #9dd7ff; font-size: 11px; font-weight: 900; text-decoration: none; white-space: nowrap; }
        .yt-admin-channel-link:hover { color: #fff; }
        .yt-admin-detail-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px 20px; }
        .yt-admin-detail-grid div { min-width: 0; }
        .yt-admin-detail-grid span { display: block; color: #777f8a; font-size: 10px; font-weight: 850; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
        .yt-admin-detail-grid strong { display: block; color: #f0f2f5; font-size: 12px; font-weight: 900; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .yt-admin-detail-coverage.complete { color: #8fd3a8; }
        .yt-admin-detail-coverage.partial { color: #d8bd77; }
        .yt-admin-detail-coverage.incomplete { color: #e48a8a; }
        .yt-admin-actions { margin-top: 16px; padding-top: 12px; border-top: 1px solid #292929; }
        .yt-admin-actions-label { color: #8f98a7; font-size: 10px; font-weight: 950; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px; }
        .yt-admin-actions-row { display: flex; flex-wrap: wrap; gap: 8px; }
        .yt-admin-actions-row button { height: 30px; padding: 0 10px; border: 1px solid #333; border-radius: 7px; font-size: 11px; font-weight: 900; }
        .yt-admin-actions-row .yt-admin-action-active { border-color: rgba(63,169,245,.55); background: rgba(63,169,245,.13); color: #9dd7ff; cursor: pointer; }
        .yt-admin-actions-row .yt-admin-action-active:hover { background: rgba(63,169,245,.24); color: #fff; }
        .yt-admin-actions-row .yt-admin-action-disabled { background: #181818; color: #666; cursor: not-allowed; }
        .yt-admin-not-connected { margin-top: 8px; color: #666; font-size: 11px; font-weight: 750; }
        .yt-admin-empty, .yt-admin-error { padding: 34px; text-align: center; color: #858b94; font-size: 13px; font-weight: 800; }
        .yt-admin-error { color: #e48a8a; }
        .yt-admin-skeleton { height: 48px; border-radius: 7px; margin-bottom: 6px; background: linear-gradient(90deg, #101010, #1b1b1b, #101010); background-size: 240px 100%; animation: ytAdminShimmer 1.3s linear infinite; }
        @keyframes ytAdminShimmer { from { background-position: -240px 0; } to { background-position: 240px 0; } }
        @container mg-viewport (max-width: 980px) { .yt-admin-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .yt-admin-status-strip { grid-template-columns: 1fr; } .yt-admin-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .yt-admin-header { align-items: flex-start; flex-direction: column; } .yt-admin-header-tools { width: 100%; flex-wrap: wrap; } }
        @container mg-viewport (max-width: 560px) { .yt-admin-page { padding: 16px 12px 30px; } .yt-admin-summary { grid-template-columns: 1fr; } .yt-admin-detail { padding-left: 18px; } .yt-admin-detail-grid { grid-template-columns: 1fr; } .yt-admin-controls { align-items: flex-start; flex-direction: column; } .yt-admin-search { width: 100%; } }
      `}</style>

      <div className="yt-admin-shell">
        <header className="yt-admin-header">
          <div>
            <div className="yt-admin-kicker">mindGAME internal operations</div>
            <h1 className="yt-admin-title">YouTube Admin</h1>
            <div className="yt-admin-subtitle">Channel coverage, backfill progress, and ingestion readiness.</div>
          </div>
          <div className="yt-admin-header-tools">
            <div className="yt-admin-add">
              <Plus size={15} color="#777" aria-hidden="true" />
              <input value={addValue} onChange={(e) => setAddValue(e.target.value)} placeholder="@ChannelHandle" aria-label="Add YouTube channel" />
              <button type="button" disabled={!canAddChannel || submitting} onClick={submitChannel} title={canAddChannel ? "Start channel ingestion" : "Enter a handle such as @SomeChannel"}>
                {submitting ? "Starting..." : "Add Channel"}
              </button>
            </div>
            <button type="button" className="yt-admin-refresh" onClick={() => loadChannels()} disabled={loading} title="Refresh channel list">
              <RefreshCw size={14} className={loading ? "yt-admin-spin" : ""} /> Refresh
            </button>
          </div>
        </header>

        {addError && <div className="yt-admin-ingestion-error">{addError}</div>}

        {ingestionJob && (
          <section className={`yt-admin-ingestion ${ingestionJob.status}`} aria-live="polite">
            <div className="yt-admin-ingestion-header">
              <div>
                <div className="yt-admin-panel-title">YouTube ingestion job</div>
                <div className="yt-admin-panel-copy">{ingestionJob.handle || normalizedHandle} {ingestionJob.channel_name ? `· ${ingestionJob.channel_name}` : ""}</div>
              </div>
              <div className={`yt-admin-job-badge ${ingestionJob.status}`}>{jobStageLabel(ingestionJob.stage || ingestionJob.status)}</div>
            </div>
            <div className="yt-admin-job-progress-row">
              <div className="yt-admin-job-progress-track"><div className="yt-admin-job-progress-fill" style={{ width: `${Math.min(100, Math.max(0, Number(ingestionJob.progress_percent) || 0))}%` }} /></div>
              <strong>{Number(ingestionJob.progress_percent || 0).toFixed(1)}%</strong>
            </div>
            <div className="yt-admin-job-message">{ingestionJob.message || jobStageLabel(ingestionJob.stage)}</div>
            <div className="yt-admin-job-metrics">
              <span>Reported <strong>{fmtInt(ingestionJob.reported_videos)}</strong></span>
              {ingestionJob.kind === "scan" && <span>Pages <strong>{fmtInt(ingestionJob.playlist_pages_scanned)}</strong></span>}
              {ingestionJob.kind === "scan" && <span>Uploads examined <strong>{fmtInt(ingestionJob.upload_ids_examined)}</strong></span>}
              {ingestionJob.kind === "scan" && <span>New videos <strong>{fmtInt(ingestionJob.new_videos_found)}</strong></span>}
              {ingestionJob.kind === "refresh" && <span>Total videos <strong>{fmtInt(ingestionJob.total_videos)}</strong></span>}
              {ingestionJob.kind === "refresh" && <span>Unavailable <strong>{fmtInt(ingestionJob.unavailable_videos)}</strong></span>}
              <span>IDs found <strong>{fmtInt(ingestionJob.video_ids_found)}</strong></span>
              <span>Processed <strong>{fmtInt(ingestionJob.videos_processed)}</strong></span>
              <span>Added <strong>{fmtInt(ingestionJob.videos_added)}</strong></span>
              {ingestionJob.kind === "refresh" && <span>Batches <strong>{fmtInt(ingestionJob.batches_processed)} / {fmtInt(ingestionJob.total_batches)}</strong></span>}
              {ingestionJob.kind === "refresh" && <span>Inserted <strong>{fmtInt(ingestionJob.snapshots_inserted)}</strong></span>}
              {ingestionJob.kind === "refresh" && <span>Updated <strong>{fmtInt(ingestionJob.snapshots_updated)}</strong></span>}
              <span>API calls <strong>{fmtInt(ingestionJob.api_calls)}</strong></span>
              <span>Search calls <strong>{fmtInt(ingestionJob.search_api_calls)}</strong></span>
              {ingestionJob.status === "complete" && <span>Coverage <strong>{Number(ingestionJob.coverage_percent || 100).toFixed(2)}%</strong></span>}
            </div>
            {ingestionJob.error && <div className="yt-admin-ingestion-error">{ingestionJob.error}</div>}
            {ingestionJob.status === "complete" && <div className="yt-admin-ingestion-success">{ingestionJob.kind === "scan" ? "Scan complete. Channel data has been refreshed below." : "Ingestion complete. Channel data has been refreshed below."}</div>}
            {ingestionJob.status === "failed" && <div className="yt-admin-ingestion-failed">{ingestionJob.kind === "scan" ? "Scan failed. Correct the issue and try the scan again." : "Ingestion failed. Correct the issue and submit the handle again."}</div>}
            <button type="button" className="yt-admin-dismiss-job" onClick={() => setIngestionJob(null)}>Dismiss job</button>
          </section>
        )}

        <section className="yt-admin-summary" aria-label="YouTube channel summary">
          <SummaryCard label="Tracked Channels" value={fmtInt(summary.tracked_channels)} icon={Radio} tone="blue" />
          <SummaryCard label="Active Channels" value={fmtInt(summary.active_channels)} icon={Activity} />
          <SummaryCard label="Indexed Videos" value={fmtInt(summary.total_indexed_videos)} icon={Database} />
          <SummaryCard label="YouTube Reported Videos" value={fmtInt(summary.total_reported_videos)} icon={Database} />
        </section>

        <section className="yt-admin-status-strip">
          <div className="yt-admin-panel">
            <div className="yt-admin-panel-title">YouTube API / Job Status</div>
            <div className="yt-admin-panel-copy">Quota usage and automated-job telemetry are not connected yet.</div>
            <div className="yt-admin-status-items">
              <div className="yt-admin-status-item"><i className="yt-admin-status-dot" /> API telemetry not connected</div>
              <div className="yt-admin-status-item"><i className="yt-admin-status-dot" /> Job telemetry not connected</div>
            </div>
          </div>
          <div className="yt-admin-panel">
            <div className="yt-admin-panel-title">Coverage legend</div>
            <div className="yt-admin-panel-copy">Coverage compares indexed videos with YouTube's reported video count.</div>
            <div className="yt-admin-status-items">
              <div className="yt-admin-status-item"><i className="yt-admin-status-dot" style={{ background: "#8fd3a8" }} /> Complete</div>
              <div className="yt-admin-status-item"><i className="yt-admin-status-dot" style={{ background: "#d8bd77" }} /> Partial</div>
              <div className="yt-admin-status-item"><i className="yt-admin-status-dot" style={{ background: "#e48a8a" }} /> Incomplete</div>
            </div>
          </div>
        </section>

        <section className="yt-admin-panel">
          <div className="yt-admin-controls">
            <div className="yt-admin-search">
              <Search size={15} className="yt-admin-search-icon" aria-hidden="true" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search channels, handles, IDs, or status..." aria-label="Search YouTube channels" />
            </div>
            <div className="yt-admin-count">Showing {visibleChannels.length} of {channels.length} channels</div>
          </div>

          {loading ? (
            <div>{Array.from({ length: 6 }).map((_, i) => <div key={i} className="yt-admin-skeleton" />)}</div>
          ) : error ? (
            <div className="yt-admin-error">{error}</div>
          ) : visibleChannels.length === 0 ? (
            <div className="yt-admin-empty">No channels match this search.</div>
          ) : (
            <div className="yt-admin-table-wrap">
              <table className="yt-admin-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort("channel_name")}>Channel</th>
                    <th onClick={() => requestSort("handle")}>Handle</th>
                    <th onClick={() => requestSort("indexed_video_count")}>Indexed Videos</th>
                    <th onClick={() => requestSort("reported_video_count")}>YouTube Reported</th>
                    <th onClick={() => requestSort("coverage_percent")}>Coverage</th>
                    <th onClick={() => requestSort("subscriber_count")}>Subscribers</th>
                    <th onClick={() => requestSort("is_active")}>Status</th>
                    <th onClick={() => requestSort("last_video_scan_at")}>Last Video Scan</th>
                    <th onClick={() => requestSort("last_stats_refresh_at")}>Last Stats Refresh</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleChannels.map((channel) => {
                    const expanded = expandedId === channel.internal_channel_id;
                    return (
                      <>
                        <tr key={channel.internal_channel_id} className={`channel-row ${expanded ? "expanded" : ""}`} onClick={() => setExpandedId(expanded ? null : channel.internal_channel_id)}>
                          <td><div className="yt-admin-channel-name"><span className="yt-admin-channel-chevron">{expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>{channel.channel_name || "Unnamed channel"}</div></td>
                          <td className="yt-admin-handle">{channel.handle || "-"}</td>
                          <td>{fmtInt(channel.indexed_video_count)}</td>
                          <td>{fmtInt(channel.reported_video_count)}</td>
                          <td><Coverage channel={channel} /></td>
                          <td>{fmtInt(channel.subscriber_count)}</td>
                          <td><span className={`yt-admin-status ${channel.is_active ? "" : "paused"}`}>{channel.is_active ? "Active" : "Paused"}</span></td>
                          <td>{fmtDate(channel.last_video_scan_at)}</td>
                          <td>{fmtDate(channel.last_stats_refresh_at)}</td>
                        </tr>
                        {expanded && <tr key={`${channel.internal_channel_id}-detail`} className="yt-admin-detail-row"><td colSpan={9}><DetailPanel channel={channel} onScan={scanChannel} scanBusy={ingestionJob?.kind === "scan" && ingestionJob.internal_channel_id === channel.internal_channel_id && ["queued", "running"].includes(ingestionJob.status)} onRefreshStats={refreshStats} refreshBusy={ingestionJob?.kind === "refresh" && ingestionJob.internal_channel_id === channel.internal_channel_id && ["queued", "running"].includes(ingestionJob.status)} /></td></tr>}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
